import { ReactElement } from 'react';
import { Box, HStack, Text } from '@chakra-ui/react';
import { useIsScrolled } from 'hooks/utils/useIsScrolled';
import { ShareIOSIcon } from 'assets/icons';
import {
  ChevronLeftIcon,
  FavoriteIcon,
  FavoriteSolidIcon,
  SuccessIcon,
  SuccessSolidIcon,
} from 'assets/icons/v2';
import { Z_INDEX } from 'styles/zIndex';
import { useCurrentRoute } from 'hooks/utils/useCurrentRoute';
import { useLocation } from 'react-router-dom';
import { ProtectedRoutes } from 'types/Routes';
import { useFirebase } from 'hooks/useFirebase';
import { NovaIconButton } from 'components/ui/NovaIconButton';

type TopBarSecondaryProps = {
  title?: string;
  hasShareButton?: boolean;
  hasLikeButton?: boolean;
  isLiked?: boolean | null;
  handleLikeChange?: () => void;
  hasReadButton?: boolean;
  isRead?: boolean | null;
  handleReadChange?: () => void;
  shareData?: ShareData;
  onBackClick: () => void;
};

export const TopBarSecondary = ({
  title,
  onBackClick,
  hasLikeButton = false,
  handleLikeChange,
  isLiked = false,
  hasReadButton = false,
  handleReadChange,
  isRead = false,
  shareData,
  hasShareButton = false,
}: TopBarSecondaryProps): ReactElement => {
  const location = useLocation();
  const { currentRoute } = useCurrentRoute(location);
  const { logFirebaseClick } = useFirebase();
  const scrollThresholdInPx = 100;
  const isFloating = useIsScrolled(scrollThresholdInPx);
  const share = (): void => {
    if (navigator.share && shareData) {
      const isMedication = currentRoute === ProtectedRoutes.MEDICATION;
      logFirebaseClick('share', shareData.title || 'missing title', {
        contentType: isMedication ? 'medication' : 'article',
      });
      navigator.share(shareData);
    }
  };

  const isLikeLoaded = isLiked !== null;
  const shouldShowLikeButton = isLikeLoaded && hasLikeButton;

  const isReadLoaded = isRead !== null;
  const shouldShowReadButton = isReadLoaded && hasReadButton;

  return (
    <Box
      top="0"
      w="full"
      display="flex"
      justifyContent="space-between"
      h="calc(env(safe-area-inset-top) + 50px)"
      pt="env(safe-area-inset-top)"
      zIndex={Z_INDEX.NAVBAR}
      bg={isFloating ? '#fff' : 'none'}
      px="3"
      boxShadow={isFloating ? '0px 0px 4px rgba(0, 0, 0, 0.1)' : 'none'}
    >
      <NovaIconButton
        variant="tertiaryCircular"
        icon={ChevronLeftIcon}
        color="text.secondary"
        onClick={onBackClick}
        shadow={isFloating ? 'none' : undefined}
        borderColor={isFloating ? 'transparent' : undefined}
      />
      {isFloating && (
        <Box display="flex" justifyContent="center" width="100%" alignItems="center" mx="2">
          <Text as="h3" textStyle="h3.medium" color="text.secondary" lineClamp={1}>
            {title}
          </Text>
        </Box>
      )}
      <HStack gap="5">
        {hasShareButton && (
          <Box
            pos="relative"
            w="28px"
            onClick={share}
            visibility={['visible', 'hidden']}
            _hover={{
              cursor: 'pointer',
            }}
          >
            <Box
              w="28px"
              bg="background.primary"
              h="28px"
              borderRadius="100%"
              bgColor="background.primary"
              filter={isFloating ? '' : 'drop-shadow(0px 0px 8px rgba(0, 0, 0, 0.13))'}
            />
            <ShareIOSIcon
              color="text.secondary"
              opacity="1"
              pos="absolute"
              top="50%"
              left="calc(50% - 1px)"
              transform="translate(-50%, -50%)"
            />
          </Box>
        )}
        {shouldShowReadButton && (
          <NovaIconButton
            variant="tertiaryCircular"
            icon={isRead ? SuccessSolidIcon : SuccessIcon}
            color="icon.accent"
            onClick={handleReadChange}
            shadow={isFloating ? 'none' : undefined}
            borderColor={isFloating ? 'transparent' : undefined}
          />
        )}
        {shouldShowLikeButton && (
          <NovaIconButton
            variant="tertiaryCircular"
            icon={isLiked ? FavoriteSolidIcon : FavoriteIcon}
            color="icon.accent"
            onClick={handleLikeChange}
            shadow={isFloating ? 'none' : undefined}
            borderColor={isFloating ? 'transparent' : undefined}
          />
        )}
      </HStack>
    </Box>
  );
};
